/**
 * @file src/_modulos/login/visuais/form/Layout.tsx
 * @description O `LoginCardLayout` é um componente de layout para o cartão de login,
 * apresentando uma interface fixa com o formulário de login. Ele inclui elementos visuais
 * como um logotipo, ícones decorativos, título, descrição e o formulário de autenticação.
 */

"use client";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Logo,
} from "visuais"; // Componentes de cartão e o Logo da biblioteca `visuais`.
import { Shield, Sparkles } from "lucide-react"; // Ícones de escudo e brilhos da biblioteca Lucide React.
import { LoginFormFields } from "@login"; // Componente do formulário de login.
import { JSX } from "react";

/**
 * @function LoginCardLayout
 * @description Componente React que renderiza o layout visual de um cartão de login.
 * Ele exibe o logotipo da aplicação, um título de boas-vindas, uma breve descrição
 * e incorpora o componente `LoginFormFields` para a entrada das credenciais do usuário.
 * Este layout é estático, sem animações, e é projetado para uma apresentação de login direta.
 * @returns {JSX.Element} Um componente `Card` estilizado contendo os elementos do layout de login.
 */
export const LoginCardLayout = (): JSX.Element => {
  return (
    <Card className="relative p-4 py-12 border border-sky-500/20 overflow-hidden w-[400px]">
      <CardHeader className="relative flex flex-col items-center gap-4 text-center">
        <div className="transform-gpu"> 
          <div className="relative">
            <Logo className="size-20" color={
                process.env.NEXT_PUBLIC_ENV === "test"
                  ? "var(--destructive)"
                  : "var(--primary)"
              } />
            <Shield className={`absolute -top-2 -right-2 w-4 h-4 opacity-60 ${process.env.NEXT_PUBLIC_ENV === "test" ? "text-destructive" : "text-sky-400"}`} />
            <Sparkles className={`absolute -bottom-1 -left-2 w-3 h-3 opacity-70 ${process.env.NEXT_PUBLIC_ENV === "test" ? "text-destructive" : "text-cyan-400"}`} />
          </div>
        </div>
        <div className="space-y-2">
          <CardTitle className={`text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent ${process.env.NEXT_PUBLIC_ENV === "test" ? "from-red-300 via-rose-400 to-rose-500" : "from-cyan-400 via-sky-400 to-blue-400"}`}>
            Bem-vindo ao RodoApp{process.env.NEXT_PUBLIC_ENV === "test" ? " - DEV" : ""}
          </CardTitle>
          <CardDescription className="text-muted-foreground/80">
            Use seu acesso do Protheus para entrar
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <LoginFormFields />
      </CardContent>
    </Card>
  );
};